import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Helmet } from "@app/components/atoms";
import { useTranslation } from "react-i18next";
import { Search } from "lucide-react";
import { Box, Skeleton, Typography } from "@mui/material";
import { clearMachineReportsPreferences } from "@app/helpers";
import {
  apiAcrossGroupMachines,
  type AcrossGroupMachinesGroup,
} from "@app/api";
import "./AcrossGroupMachines.scss";

function GroupsBlockSkeleton() {
  return (
    <Box sx={{ mt: 3 }}>
      <Skeleton variant="text" width={200} height={32} sx={{ mb: 1 }} />
      <Skeleton variant="rounded" height={200} sx={{ borderRadius: 1 }} />
    </Box>
  );
}

export function AcrossGroupMachines() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const qFromUrl = searchParams.get("q") ?? "";

  const [query, setQuery] = useState(qFromUrl);
  const searchTerm = qFromUrl.trim();

  const [groups, setGroups] = useState<AcrossGroupMachinesGroup[]>([]);
  const [summaryText, setSummaryText] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    clearMachineReportsPreferences();
  }, []);

  useEffect(() => {
    setQuery(qFromUrl);
  }, [qFromUrl]);

  useEffect(() => {
    if (!searchTerm) {
      setGroups([]);
      setSummaryText("");
      setLoading(false);
      return;
    }

    let cancelled = false;

    (async () => {
      setLoading(true);
      setSummaryText("");
      setGroups([]);

      try {
        const res = await apiAcrossGroupMachines(searchTerm);
        if (cancelled) return;

        const payload = res.data?.data;
        setSummaryText(payload?.machines ?? "");
        setGroups(payload?.groups ?? []);
      } catch (e) {
        console.error("acrossGroupMachines:", e);
        if (!cancelled) {
          setGroups([]);
          setSummaryText("");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [searchTerm]);

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== "Enter") return;
    if (e.nativeEvent.isComposing) return;
    e.preventDefault();
    const trimmed = query.trim();
    setSearchParams(trimmed ? { q: trimmed } : {}, { replace: true });
  };

  const handleQueryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = e.target.value;
    setQuery(next);
    if (next.trim() === "") {
      setSearchParams({}, { replace: true });
    }
  };

  const clearSearch = () => {
    setQuery("");
    setSearchParams({}, { replace: true });
  };

  return (
    <div className="across-group-machines-page group-list-page">
      <Helmet title={t("metaInfo.acrossGroupMachinesSearch")} />
      <div className="container">
        <div className="row">
          <div className="sp-p24 across-group-machines-page__inner">
            <div className="across-group-machines-page__search-wrap">
              <Search
                className="across-group-machines-page__search-icon"
                aria-hidden
              />
              <input
                type="text"
                className="across-group-machines-page__input"
                value={query}
                onChange={handleQueryChange}
                onKeyDown={handleSearchKeyDown}
                placeholder={t("Customermachinenumber")}
                autoComplete="off"
                enterKeyHint="search"
                aria-label={t("Customermachinenumber")}
              />
              {query ? (
                <button
                  type="button"
                  className="across-group-machines-page__clear"
                  onClick={clearSearch}
                  aria-label={t("clear")}
                >
                  ×
                </button>
              ) : null}
            </div>

            <div
              className="across-group-machines-page__results fs-16 txt-gray-1"
              role="status"
              aria-live="polite"
            >
              {loading && searchTerm ? (
                <Skeleton variant="text" width={220} height={24} />
              ) : (
                summaryText
              )}
            </div>

            {loading && searchTerm ? (
              <>
                <GroupsBlockSkeleton />
                <GroupsBlockSkeleton />
              </>
            ) : (
              groups.map((group) => {
                const rows = group.machines ?? [];

                return (
                  <section
                    key={group.groupId}
                    className="across-group-machines-page__group"
                  >
                    <div className="across-group-machines-page__group-header">
                      <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                        {group.groupName}
                      </Typography>
                    </div>

                    <div className="across-group-machines-page__table-wrap">
                      <table className="data-table across-group-machines-page__table">
                        <thead>
                          <tr>
                            <th>{t("machineInfo.customerMachineNumber")}</th>
                            <th>{t("machineManufacturerName")}</th>
                            <th>{t("machineInfo.machineType")}</th>
                            <th>{t("machineInfo.modelType")}</th>
                            <th>{t("machineInfo.serialNo")}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rows.length === 0 ? (
                            <tr>
                              <td
                                colSpan={5}
                                className="across-group-machines-page__empty-cell"
                              >
                                —
                              </td>
                            </tr>
                          ) : (
                            rows.map((row) => (
                              <tr key={row.machineId}>
                                <td>
                                  <div className="across-group-machines-page__machine-cell">
                                    {row.pictureUrl ? (
                                      <img
                                        src={row.pictureUrl}
                                        alt=""
                                        className="across-group-machines-page__thumb"
                                        onError={(ev) => {
                                          (
                                            ev.target as HTMLImageElement
                                          ).style.visibility = "hidden";
                                        }}
                                      />
                                    ) : (
                                      <span className="across-group-machines-page__thumb-placeholder" />
                                    )}
                                    <Link
                                      className="txt-link across-group-machines-page__machine-link"
                                      to={`/groups/${group.groupId}/machines/${row.machineId}/inspection-history`}
                                    >
                                      {row.customerMachineName}
                                    </Link>
                                  </div>
                                </td>
                                <td>{row.machineManufacturerName}</td>
                                <td>{row.machineTypeName}</td>
                                <td>{row.modelAndType}</td>
                                <td>{row.serialNumber}</td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </section>
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
